import React, { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { currentCategories, applyCategories, ICONS, MARKER_EMOJIS, MAP_ICONS, MarkerGlyph } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";

const SWATCHES = [
  "#7F1D1D", "#B91C1C", "#DC2626", "#EF4444", "#F87171", "#E11D48", "#F43F5E", "#BE185D", "#DB2777", "#EC4899", "#F472B6",
  "#9A3412", "#C2410C", "#EA580C", "#F97316", "#FB923C", "#B45309", "#D97706", "#F59E0B", "#CA8A04", "#EAB308", "#FACC15",
  "#3F6212", "#4D7C0F", "#65A30D", "#84CC16", "#166534", "#15803D", "#16A34A", "#22C55E", "#047857", "#059669", "#10B981", "#34D399",
  "#0F766E", "#0D9488", "#14B8A6", "#0E7490", "#0891B2", "#06B6D4", "#0369A1", "#0284C7", "#0EA5E9", "#38BDF8",
  "#1E3A8A", "#1D4ED8", "#2563EB", "#3B82F6", "#60A5FA", "#3730A3", "#4338CA", "#4F46E5", "#6366F1", "#818CF8",
  "#5B21B6", "#6D28D9", "#7C3AED", "#8B5CF6", "#A78BFA", "#86198F", "#A21CAF", "#C026D3", "#D946EF", "#E879F9",
  "#78350F", "#92400E", "#A16207", "#57534E", "#78716C", "#0F172A", "#1E293B", "#334155", "#475569", "#64748B", "#94A3B8",
];
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ë/g, "e").replace(/ç/g, "c").replace(/[^a-z0-9]/g, "").slice(0, 30);

function EmojiGrid({ value, color, onPick, tid, withImages }) {
  const opts = withImages ? [...MAP_ICONS, ...MARKER_EMOJIS] : MARKER_EMOJIS;
  return (
    <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-1 bg-slate-50 rounded-xl">
      {opts.map((e) => (
        <button key={e} type="button" data-testid={`${tid}-${e}`} onClick={() => onPick(e)} className={`w-9 h-9 rounded-full flex items-center justify-center text-lg ${value === e ? "ring-2 ring-slate-900" : ""}`} style={{ background: value === e ? color || "#64748B" : "#fff" }}><MarkerGlyph v={e} /></button>
      ))}
    </div>
  );
}

function SubsEditor({ subs, emojis, catEmoji, color, onChange, i }) {
  const [v, setV] = useState("");
  const [pick, setPick] = useState(null);
  const add = () => { const s = v.trim(); if (!s || subs.includes(s)) return; onChange([...subs, s], emojis); setV(""); };
  const rename = (j, name) => {
    const old = subs[j];
    const e = { ...emojis };
    if (e[old] !== undefined) { e[name] = e[old]; delete e[old]; }
    onChange(subs.map((x, k) => (k === j ? name : x)), e);
  };
  const remove = (j) => { const e = { ...emojis }; delete e[subs[j]]; onChange(subs.filter((_, k) => k !== j), e); };
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 mb-1">Nënkategoritë dhe marker-i i secilës</div>
      <div className="space-y-1.5">
        {subs.map((s, j) => (
          <div key={j}>
            <div className="flex gap-1.5">
              <button type="button" data-testid={`cat-sub-emoji-${i}-${j}`} onClick={() => setPick(pick === j ? null : j)} className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-lg border border-slate-200" style={{ opacity: emojis[s] ? 1 : 0.45 }} aria-label="Marker"><MarkerGlyph v={emojis[s] || catEmoji} /></button>
              <Input data-testid={`cat-sub-${i}-${j}`} value={s} onChange={(e) => rename(j, e.target.value)} className="rounded-lg h-9 text-sm" />
              <button type="button" data-testid={`cat-sub-del-${i}-${j}`} onClick={() => remove(j)} className="w-9 h-9 shrink-0 rounded-lg bg-red-50 text-red-500 flex items-center justify-center" aria-label="Hiq"><X className="w-4 h-4" /></button>
            </div>
            {pick === j && (
              <div className="mt-1.5">
                <EmojiGrid withImages value={emojis[s]} color={color} tid={`cat-sub-emoji-pick-${i}-${j}`} onPick={(e) => { onChange(subs, { ...emojis, [s]: e }); setPick(null); }} />
                {emojis[s] && <button type="button" onClick={() => { const e = { ...emojis }; delete e[s]; onChange(subs, e); setPick(null); }} className="text-[11px] text-slate-500 mt-1">Përdor marker-in e kategorisë</button>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1.5">
        <Input data-testid={`cat-sub-new-${i}`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Shto nënkategori..." className="rounded-lg h-9 text-sm" />
        <Button type="button" data-testid={`cat-sub-add-${i}`} onClick={add} className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function CatRow({ c, i, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[c.icon] || ICONS.Store;
  return (
    <div className="bg-white rounded-2xl border border-slate-100" data-testid={`cat-row-${c.key}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`cat-toggle-${c.key}`} className="w-full flex items-center gap-2.5 p-3 text-left">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: c.color || "#64748B" }}><Icon className="w-4 h-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold text-sm text-slate-800 truncate">{c.label} {c.custom && <span className="text-[10px] text-emerald-600 font-bold">E RE</span>}</span>
          <span className="block text-xs text-slate-400 truncate">{c.subs.length} nënkategori · {c.emoji}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <Input data-testid={`cat-label-${c.key}`} value={c.label} onChange={(e) => onChange({ ...c, label: e.target.value })} placeholder="Emri" className="rounded-xl h-10" />
          <Input data-testid={`cat-tagline-${c.key}`} value={c.tagline} onChange={(e) => onChange({ ...c, tagline: e.target.value })} placeholder="Përshkrimi i shkurtër" className="rounded-xl h-10" />
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Ngjyra</div>
            <div className="flex flex-wrap gap-1.5 items-center max-h-48 overflow-y-auto p-1">
              {SWATCHES.map((h) => (
                <button key={h} type="button" data-testid={`cat-color-${c.key}-${h.slice(1)}`} onClick={() => onChange({ ...c, color: h })} className={`w-8 h-8 rounded-full ${c.color?.toUpperCase() === h ? "ring-2 ring-offset-2 ring-slate-900" : ""}`} style={{ background: h }} aria-label={h} />
              ))}
              <input type="color" data-testid={`cat-color-custom-${c.key}`} value={c.color || "#64748B"} onChange={(e) => onChange({ ...c, color: e.target.value.toUpperCase() })} className="w-8 h-8 rounded-full border-0 p-0 bg-transparent" />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Ikona</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(ICONS).map(([n, I]) => (
                <button key={n} type="button" data-testid={`cat-icon-${c.key}-${n}`} onClick={() => onChange({ ...c, icon: n })} className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.icon === n ? "text-white" : "bg-slate-100 text-slate-600"}`} style={c.icon === n ? { background: c.color || "#64748B" } : undefined}><I className={n.startsWith("Img_") ? "w-6 h-6" : "w-4 h-4"} /></button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">Marker-i i kategorisë në hartë</div>
            <EmojiGrid value={c.emoji} color={c.color} tid={`cat-emoji-${c.key}`} onPick={(e) => onChange({ ...c, emoji: e })} />
          </div>
          {c.key !== "komunitete" && <SubsEditor subs={c.subs} emojis={c.sub_emojis || {}} catEmoji={c.emoji || "📍"} color={c.color} i={c.key} onChange={(subs, sub_emojis) => onChange({ ...c, subs, sub_emojis })} />}
          {c.custom && <button type="button" onClick={onDelete} data-testid={`cat-del-${c.key}`} className="flex items-center gap-1 text-xs font-semibold text-red-500"><Trash2 className="w-3.5 h-3.5" />Hiq kategorinë</button>}
        </div>
      )}
    </div>
  );
}

export default function AdminCategories() {
  const { confirm } = useConfirm();
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/categories").then(({ data }) => { applyCategories(data.items); setList(currentCategories()); }).catch(() => setFailed(true));
  }, []);
  if (!list) return <div data-testid="admin-categories-loading" className="p-6 text-center text-sm text-slate-500">{failed ? "Kategoritë nuk u ngarkuan. Rifresko faqen." : "Duke ngarkuar kategoritë..."}</div>;

  const add = () => {
    const key = slug(name);
    if (!name.trim() || !key) return toast.error("Shkruaj emrin e kategorisë");
    if (list.some((c) => c.key === key)) return toast.error("Kjo kategori ekziston");
    setList([...list, { key, label: name.trim(), tagline: "", color: "#6366F1", icon: "Store", emoji: "📍", subs: ["Të tjera"], sub_emojis: {}, custom: true }]);
    setName("");
  };
  const del = async (i) => {
    if (!(await confirm({ title: "Hiq kategorinë?", message: `${list[i].label} do të hiqet nga faqja.`, confirmText: "Hiq" }))) return;
    setList(list.filter((_, j) => j !== i));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/categories", { items: list });
      applyCategories(data.items);
      toast.success("Kategoritë u ruajtën");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2" data-testid="admin-categories">
      <div className="bg-white rounded-2xl border border-slate-100 p-3">
        <h3 className="text-sm font-bold text-slate-900 mb-2">Shto kategori të re</h3>
        <div className="flex gap-2">
          <Input data-testid="cat-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="p.sh. Bukuri & Shëndet" className="rounded-xl h-10" />
          <Button type="button" onClick={add} data-testid="cat-add" className="rounded-xl h-10 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /></Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">Kategoritë e reja përdorin formularin e Ushqim / Pije. Kategoritë ekzistuese nuk fshihen.</p>
      </div>
      {list.map((c, i) => (
        <CatRow key={c.key} c={c} i={i} onChange={(n) => setList(list.map((x, j) => (j === i ? n : x)))} onDelete={() => del(i)} />
      ))}
      <Button onClick={save} disabled={saving} data-testid="cat-save" className="sticky bottom-24 w-full rounded-xl h-12 bg-emerald-600 hover:bg-emerald-700 shadow-lg">{saving ? "Duke ruajtur..." : "Ruaj kategoritë"}</Button>
    </div>
  );
}
